"use client";
import Link from "next/link";
import { MenuIcon } from "lucide-react";
import { Fragment } from "react";
import type { WpMenuItem } from "@nextwp/core";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { SiteLogo } from "../site-logo";
import Button from "../ui/button";
import { MenuItemLink } from "./menu-item-link";

interface MenuItem extends WpMenuItem {
  childItems?: MenuItem[];
}

export function MobileMenu({
  menuItems,
  className,
  contact,
  logo,
  page_slug_to_hide_navbar,
}: {
  menuItems?: MenuItem[];
  className?: string;
  contact?: any;
  logo?: any;
  page_slug_to_hide_navbar?: any;
}) {
  const pathname = usePathname();

  const hideNavBar = page_slug_to_hide_navbar.some(
    (page) => page.slug === pathname,
  );

  if (hideNavBar) {
    return;
  }
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          aria-label="menu"
          className={cn("p-2", className)}
          type="button"
        >
          <MenuIcon className="text-green" />
        </button>
      </SheetTrigger>
      <SheetContent className="flex max-h-screen !w-screen flex-col justify-between overflow-y-scroll bg-primary p-0 text-white">
        <div>
          <div className="edges py-5">
            <SheetTrigger asChild>
              <Link className="mb-10" href="/">
                <SiteLogo
                  className="h-[41.64px] w-[52.36px] flex-shrink-0"
                  logo={logo}
                />
              </Link>
            </SheetTrigger>
          </div>

          <div className="edges">
            {menuItems?.map((item: MenuItem, index: number) => {
              if (item.childItems?.length) {
                return (
                  <Fragment key={index}>
                    <Accordion
                      className="w-full"
                      collapsible={menuItems[0].id !== item.id}
                      defaultValue={
                        menuItems[0].id === item.id ? item.id : null
                      }
                      type="single"
                    >
                      <AccordionItem className="border-b-0" value={item.id}>
                        <AccordionTrigger className="h-auto py-1 text-lg font-medium text-accent">
                          <span>{item.label}</span>
                        </AccordionTrigger>

                        <AccordionContent>
                          {item.childItems.map((childItem: MenuItem) => {
                            return (
                              <SheetTrigger asChild key={childItem.id}>
                                <MenuItemLink
                                  description={childItem.description}
                                  label={childItem.label || ""}
                                  path={childItem.path || ""}
                                />
                              </SheetTrigger>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Fragment>
                );
              }

              return (
                <SheetTrigger asChild key={index}>
                  <MenuItemLink
                    className=""
                    description={item.acf?.subline}
                    label={item.label || ""}
                    path={item.path || ""}
                  />
                </SheetTrigger>
              );
            })}
          </div>
        </div>

        <div className="flex items-center border-t">
          {contact?.phone ? (
            <Button asChild>
              <a
                className="flex w-1/2 items-center justify-center gap-x-4 border-r hover:bg-white hover:text-primary"
                href={`tel:${contact.phone}`}
              >
                <svg
                  className="flex-shrink-0"
                  fill="none"
                  height="19"
                  viewBox="0 0 20 19"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.4888 14.4138C19.3216 15.6841 18.6977 16.8502 17.7337 17.6942C16.7696 18.5382 15.5313 19.0023 14.25 19.0001C6.80626 19.0001 0.750008 12.9438 0.750008 5.50005C0.747712 4.21876 1.2119 2.98045 2.05588 2.01639C2.89986 1.05234 4.06592 0.428467 5.33626 0.261304C5.6575 0.22208 5.9828 0.2878 6.26362 0.448653C6.54444 0.609506 6.7657 0.856865 6.89438 1.1538L8.87438 5.57412V5.58537C8.9729 5.81267 9.01359 6.06083 8.99282 6.30769C8.97204 6.55455 8.89044 6.79242 8.75532 7.00005C8.73845 7.02537 8.72063 7.0488 8.70188 7.07224L6.75001 9.38599C7.4522 10.8129 8.9447 12.2922 10.3903 12.9963L12.6722 11.0547C12.6946 11.0359 12.7181 11.0184 12.7425 11.0022C12.95 10.8639 13.1887 10.7794 13.437 10.7565C13.6853 10.7336 13.9354 10.7729 14.1647 10.871L14.1769 10.8766L18.5934 12.8557C18.8909 12.9839 19.1389 13.205 19.3003 13.4858C19.4616 13.7667 19.5278 14.0922 19.4888 14.4138Z"
                    fill="currentColor"
                  />
                </svg>
                Call
              </a>
            </Button>
          ) : null}

          {contact?.email ? (
            <Button asChild>
              <a
                className="flex w-1/2 items-center justify-center gap-x-4 hover:bg-white hover:text-primary"
                href={`mailto:${contact.email}`}
              >
                <svg
                  className="flex-shrink-0 text-inherit"
                  fill="none"
                  height="20"
                  viewBox="0 0 20 20"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 9.99996C20 10.0478 20 10.0937 20 10.1425C19.9681 11.4793 19.4722 13 17.375 13C15.2056 13 14.75 11.3687 14.75 9.99996V6.24996C14.7502 6.14714 14.7293 6.04538 14.6885 5.95099C14.6478 5.8566 14.5881 5.77159 14.5131 5.70126C14.4381 5.63092 14.3494 5.57676 14.2526 5.54213C14.1558 5.5075 14.0529 5.49315 13.9503 5.49996C13.7571 5.51655 13.5774 5.60579 13.4474 5.74968C13.3175 5.89357 13.2469 6.0814 13.25 6.27527V6.65027C12.5729 6.04345 11.7279 5.65596 10.8263 5.53884C9.92469 5.42171 9.00874 5.58045 8.19911 5.99413C7.38947 6.40782 6.72414 7.05705 6.29075 7.85631C5.85736 8.65557 5.67624 9.56736 5.77125 10.4716C5.86626 11.3758 6.23294 12.23 6.823 12.9218C7.41305 13.6135 8.1988 14.1102 9.07673 14.3466C9.95467 14.583 10.8836 14.5479 11.7412 14.2459C12.5988 13.9439 13.3448 13.3893 13.8809 12.655C13.9747 12.8189 14.0806 12.9756 14.1978 13.1237C14.7013 13.75 15.6603 14.5 17.375 14.5C17.6646 14.5009 17.9537 14.4774 18.2394 14.4296C18.31 14.4179 18.3825 14.4266 18.4483 14.4547C18.5142 14.4828 18.5706 14.5292 18.6109 14.5883C18.6512 14.6475 18.6738 14.7169 18.6759 14.7885C18.678 14.8601 18.6596 14.9307 18.6228 14.9921C17.7414 16.4707 16.4843 17.6897 14.9794 18.5254C13.4744 19.361 11.7751 19.7835 10.0541 19.75C4.91844 19.6468 0.70625 15.5181 0.5075 10.3853C0.456664 9.08991 0.664659 7.79742 1.11932 6.58341C1.57397 5.3694 2.26616 4.25825 3.15539 3.31495C4.04462 2.37166 5.11304 1.61516 6.29813 1.08972C7.48323 0.564286 8.76121 0.280454 10.0573 0.254834C11.3534 0.229214 12.6416 0.462319 13.8465 0.940513C15.0515 1.41871 16.149 2.13239 17.0748 3.0398C18.0006 3.94722 18.7361 5.03015 19.2384 6.22525C19.7407 7.42034 19.9996 8.7036 20 9.99996ZM7.25 9.99996C7.25 10.5933 7.42595 11.1733 7.75559 11.6667C8.08524 12.16 8.55377 12.5445 9.10195 12.7716C9.65013 12.9987 10.2533 13.0581 10.8353 12.9423C11.4172 12.8266 11.9518 12.5408 12.3713 12.1213C12.7909 11.7017 13.0766 11.1672 13.1924 10.5852C13.3081 10.0033 13.2487 9.40009 13.0216 8.85191C12.7946 8.30373 12.4101 7.83519 11.9167 7.50555C11.4234 7.17591 10.8433 6.99996 10.25 6.99996C9.45435 6.99996 8.69129 7.31603 8.12868 7.87864C7.56607 8.44125 7.25 9.20431 7.25 9.99996Z"
                    fill="currentColor"
                  />
                </svg>
                Mail
              </a>
            </Button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
